import { ThemeColorKey, ThemeTypographyKey } from "chameleon/lib/theme/theme";
import { sharedThemeValues } from "chameleon/lib/theme/theme-data";

import { textClass, textStyles } from "./text.css";

interface TextProps {
  /**
   * ID for accessibility purposes
   */
  id?: string;
  /**
   * Wrapped content
   */
  children: React.ReactNode;
  /**
   * Used for accessibility if tag is a label
   */
  htmlFor?: string;
  /**
   * Theme Color token to display
   */
  colorVariant?: ThemeColorKey;
  /**
   * Theme Typography variant
   */
  styleVariant: ThemeTypographyKey;
  /**
   * Tag to render as
   */
  tagElement?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "label" | "p" | "span";
  /**
   * Test id for unit tests
   */
  testId?: string;
  /**
   * Optional style subset
   */
  style?: {
    textDecoration?: "line-through" | "none";
  };
}

interface TagProps extends Omit<TextProps, "colorVariant"> {
  /**
   * class name
   */
  className: string;
}

const Tag = ({ styleVariant, tagElement, testId, ...otherProps }: TagProps) => {
  const tagString =
    tagElement || sharedThemeValues.typography.tags[styleVariant];

  switch (tagString) {
    case "h1":
      return <h1 data-testid={testId} {...otherProps} />;
    case "h2":
      return <h2 data-testid={testId} {...otherProps} />;
    case "h3":
      return <h3 data-testid={testId} {...otherProps} />;
    case "h4":
      return <h4 data-testid={testId} {...otherProps} />;
    case "h5":
      return <h5 data-testid={testId} {...otherProps} />;
    case "h6":
      return <h6 data-testid={testId} {...otherProps} />;
    case "label":
      return <label data-testid={testId} {...otherProps} />;
    case "p":
      return <p data-testid={testId} {...otherProps} />;
    case "span":
    default:
      return <span data-testid={testId} {...otherProps} />;
  }
};

/**
 * Primary Text component
 */
export const Text = ({
  id,
  children,
  htmlFor,
  colorVariant = "primaryText",
  ...otherProps
}: TextProps) => {
  return (
    <Tag
      id={id}
      htmlFor={htmlFor}
      className={`${textClass} ${textStyles({
        color: colorVariant,
        style: otherProps.styleVariant,
      })}`}
      {...otherProps}
    >
      {children}
    </Tag>
  );
};
